import { ChevronLeftIcon, HeartIcon, Icon } from '@web/components/atoms/Icons'
import { Box, Group } from '@web/components/atoms/Layout'
import { Text } from '@web/components/page-components/Text'
import { ComponentType, DonationFrequency } from '@web/graphql-components'
import { useDonationFormContext } from '../DonationFormProvider'
import { PageButton } from '../../PageButton'
import DonationStepTabs from './atoms/DonationStepTab'
import { DONATION_FREQUENCY_TO_LABEL } from '../donationUtils'
import { PropsWithChildren } from 'react'
import { IconButton } from '@web/components/atoms/Button'
import { DESKTOP_CONTAINER_QUERY } from '../../utils'

export const DEFAULT_UPSELL_TITLE =
  '{"root":{"children":[{"children":[{"detail":0,"format":1,"mode":"normal","style":"font-family: \\"Helvetica Neue\\", Helvetica, Arial, sans-serif;font-size: 24px;","text":"Make A Greater Impact","type":"text","version":1}],"direction":"ltr","format":"center","indent":0,"type":"paragraph","version":1}],"direction":"ltr","format":"","indent":0,"type":"root","version":1}}'
export const DEFAULT_UPSELL_MESSAGE =
  '{"root":{"children":[{"children":[{"detail":0,"format":0,"mode":"normal","style":"font-family: Arial, \\"Helvetica Neue\\", Helvetica, sans-serif;font-size: 16px;","text":"Become a regular giver and lorem ipsum dolor sit amet, consectetur adipiscing elit, sed do eiusmod tempor incididunt ut labore et dolore magna aliqua.","type":"text","version":1}],"direction":"ltr","format":"center","indent":0,"type":"paragraph","version":1}],"direction":"ltr","format":"","indent":0,"type":"root","version":1}}'

const UpsellButton = ({
  type,
  amount,
  upsellPercentage,
  frequency,
  handleClick,
}: {
  type: 'Primary' | 'Secondary'
  amount: number
  upsellPercentage: number
  frequency: DonationFrequency
  handleClick: (amount: number, frequency: DonationFrequency) => void
}) => {
  const trueAmount = Math.floor(amount * (upsellPercentage / 100))
  return (
    <PageButton
      name={type}
      buttonText={`Donate $${trueAmount} ${DONATION_FREQUENCY_TO_LABEL[frequency]}`}
      width="100%"
      onClick={() => handleClick(trueAmount, frequency)}
      styleOverride={type === 'Secondary' ? { bgColor: '#fff' } : undefined}
    />
  )
}

export const UpsellModuleHeader = () => {
  const upsellOptions = useDonationFormContext((state) => state.settings.styles?.options?.upsellRegularGivingOptions)
  const prevStep = useDonationFormContext((state) => state.actions.prevStep)

  return (
    <>
      <Group position="relative" w="100%" justifyContent="center">
        <Box position="absolute" top={0} left={0} zIndex={2}>
          <IconButton buttonAppearance="text" id="back-to-start" onClick={prevStep} size="md">
            <ChevronLeftIcon />
          </IconButton>
        </Box>
        <Group
          columnGap="16px"
          css={{
            flexDirection: 'column',
            [DESKTOP_CONTAINER_QUERY]: {
              flexDirection: 'row',
            },
          }}
        >
          <Icon boxSize="36px">
            <HeartIcon color="var(--branding-color-brand)" />
          </Icon>
          <Text
            id="upsell-header"
            type={ComponentType.Text}
            textValue={upsellOptions.heading || DEFAULT_UPSELL_TITLE}
          />
        </Group>
      </Group>
      {upsellOptions.message !== undefined && (
        <Text
          id="upsell-message"
          type={ComponentType.Text}
          textValue={upsellOptions.message || DEFAULT_UPSELL_MESSAGE}
        />
      )}
    </>
  )
}

const UpsellModule = ({ children }: PropsWithChildren) => {
  const upsellOptions = useDonationFormContext((state) => state.settings.styles?.options?.upsellRegularGivingOptions)
  const amount = useDonationFormContext((state) => state.formValues.amount) ?? 0
  const setFormValues = useDonationFormContext((state) => state.actions.setFormValues)
  const nextStep = useDonationFormContext((state) => state.actions.nextStep)

  const handleNextStep = (amount: number, frequency: DonationFrequency) => {
    setFormValues({ amount, givingFrequency: frequency })
    nextStep()
  }

  return (
    <>
      <DonationStepTabs />
      {children}
      <UpsellButton
        type="Primary"
        amount={amount}
        upsellPercentage={upsellOptions.upsells[0]}
        frequency={upsellOptions.givingOption as DonationFrequency}
        handleClick={handleNextStep}
      />
      {upsellOptions.upsells?.[1] && upsellOptions.upsells?.[1] > 0 && (
        <UpsellButton
          type="Secondary"
          amount={amount}
          upsellPercentage={upsellOptions.upsells[1]}
          frequency={upsellOptions.givingOption as DonationFrequency}
          handleClick={handleNextStep}
        />
      )}
      <PageButton
        name="Secondary"
        buttonText={`Continue with my one-time $${amount} donation`}
        styleOverride={{
          borderColor: '#000',
          color: '#000',
          bgColor: '#fff',
          textWrap: 'auto',
          height: 'auto',
          padding: '1rem',
        }}
        width="100%"
        onClick={() => handleNextStep(amount, DonationFrequency.OneTime)}
      />
    </>
  )
}

export default UpsellModule
